/* eslint-disable react-refresh/only-export-components -- `reasonText` is part of this file's contract (the list and its tests share it with the row). */
import type { TFunction } from "i18next";
import {
  AtSign,
  Bell,
  Check,
  Mail,
  Reply,
  RotateCcw,
  Send,
  Share2,
  UserRoundPlus,
  type LucideIcon,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  NotificationCloseReason,
  NotificationReason,
  type FuerDichEntryFieldsFragment,
} from "@lytra/frontend-common/__generated__/graphql";
import { Badge } from "@lytra/frontend-common/components/v2/ui/badge";
import { Button } from "@lytra/frontend-common/components/v2/ui/button";
import { IconButton } from "@lytra/frontend-common/components/v2/ui/icon-button";
import { ListItem } from "@lytra/frontend-common/components/v2/ui/list-item";
import { personName } from "@/components/customer/ticket/ticketHelpers";
import { formatDateTime, formatRelativeDateShort } from "@/lib/utils";

export type FuerDichTab = "open" | "closed";

const REASON_ICON: Record<NotificationReason, LucideIcon> = {
  [NotificationReason.CustomerReply]: Mail,
  [NotificationReason.Assigned]: UserRoundPlus,
  [NotificationReason.Mention]: AtSign,
  [NotificationReason.Share]: Share2,
  [NotificationReason.ForwardReply]: Reply,
  [NotificationReason.ForwardRequest]: Send,
};

/**
 * The reason line of an entry: its LATEST reason only (a bump replaces ASSIGNED with
 * CUSTOMER_REPLY). The actor is missing for routing, system events, impersonation and
 * deleted users, which have their own wording; a customer reply never has one. A reply to
 * a forward (#717) is written by someone who may not be a user at all, so its actor is the
 * mail's display name or address (`actorName`), not the user-based name fields.
 */
export function reasonText(
  t: TFunction,
  entry: Pick<
    FuerDichEntryFieldsFragment,
    "reason" | "actorGivenName" | "actorFamilyName"
  > &
    Partial<Pick<FuerDichEntryFieldsFragment, "actorName">>,
): string {
  if (entry.reason === NotificationReason.CustomerReply) {
    return t(`notifications.reason.${entry.reason}`);
  }
  const actor =
    entry.reason === NotificationReason.ForwardReply
      ? entry.actorName?.trim()
      : personName({
          givenName: entry.actorGivenName,
          familyName: entry.actorFamilyName,
        });
  return actor
    ? t(`notifications.reason.${entry.reason}`, { actor })
    : t(`notifications.reason.${entry.reason}_noActor`);
}

/** Reasons whose row leads to one message of the thread, not to the ticket as a whole. */
const MESSAGE_LINK_REASONS: ReadonlySet<NotificationReason> = new Set([
  NotificationReason.Mention,
  NotificationReason.Share,
  NotificationReason.ForwardReply,
  NotificationReason.ForwardRequest,
]);

/**
 * Where a row leads. A mention leads to the note or reply that holds it (`?messageId=`), a
 * share to the send, a forward reply to the colleague's mail, so the person lands on the
 * words, expanded and ringed, not just on the ticket; every other reason is about the
 * ticket as a whole. The pathname stays `/agent/<id>`, which the list's "already on that
 * ticket" check relies on.
 */
export function entryLink(
  entry: Pick<
    FuerDichEntryFieldsFragment,
    "reason" | "conversationId" | "messageId"
  >,
): string {
  const base = `/agent/${entry.conversationId}`;
  return MESSAGE_LINK_REASONS.has(entry.reason) && entry.messageId
    ? `${base}?messageId=${encodeURIComponent(entry.messageId)}`
    : base;
}

interface FuerDichEntryRowProps {
  entry: FuerDichEntryFieldsFragment;
  tab: FuerDichTab;
  /** Row click. The row is a real link, so the caller decides what a modifier click does. */
  onSelect: (
    entry: FuerDichEntryFieldsFragment,
    event: React.MouseEvent<HTMLElement>,
  ) => void;
  /** "Erledigt" (Offen tab). Never navigates and never closes the popover. */
  onDone?: (entry: FuerDichEntryFieldsFragment) => void;
  /** "Bestätigen" on an open request (Anfrage an Kollegen); the row stays. */
  onConfirm?: (entry: FuerDichEntryFieldsFragment) => void;
  /** "Wieder öffnen" (Erledigt tab). */
  onReopen?: (entry: FuerDichEntryFieldsFragment) => void;
}

/**
 * One entry of the "Für dich" list. All reasons share this shape: the ticket subject as the
 * title (two lines: tickets are told apart by the last words of their subject), then who did
 * what and when ("Anna Beispiel hat dich erwähnt · vor 2 Std."), then a quote of what was
 * written, so the agent can rank the entry without opening it. The reason icon leads the
 * row; the sentence next to it explains it. No unread dot (Offen already says "you still owe
 * something here"). The action
 * slot holds "Erledigt" (Offen) or "Wieder öffnen" (Erledigt); an entry its request withdrew
 * has no way back, so its slot stays reserved and empty.
 */
export function FuerDichEntryRow({
  entry,
  tab,
  onSelect,
  onDone,
  onConfirm,
  onReopen,
}: FuerDichEntryRowProps) {
  const { t } = useTranslation();
  const open = tab === "open";
  const time = open ? entry.lastEventAt : entry.closedAt;

  let trailing: React.ReactNode = null;
  if (!open && entry.closeReason) {
    trailing = (
      <Badge tone="neutral" size="xs">
        {t(`notifications.closeReason.${entry.closeReason}`)}
      </Badge>
    );
  }

  // An Anfrage waits for the recipient's confirmation; afterwards it says so.
  const isRequest = entry.reason === NotificationReason.ForwardRequest;
  if (open && isRequest && entry.confirmedAt) {
    trailing = (
      <Badge tone="success" size="xs">
        {t("notifications.confirmed")}
      </Badge>
    );
  }

  let actions: React.ReactNode;
  if (open) {
    actions = (
      <>
        {isRequest && !entry.confirmedAt && (
          <Button
            variant="outline"
            size="sm"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onConfirm?.(entry);
            }}
          >
            {t("notifications.actions.confirm")}
          </Button>
        )}
        <IconButton
          variant="ghost"
          size="sm"
          icon={Check}
          label={t("notifications.actions.doneHint")}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onDone?.(entry);
          }}
        />
      </>
    );
  } else if (entry.closeReason === NotificationCloseReason.Withdrawn) {
    // The slot keeps its 28px so the time and tag do not shift between rows.
    actions = <span aria-hidden="true" className="size-7" />;
  } else {
    actions = (
      <IconButton
        variant="ghost"
        size="sm"
        icon={RotateCcw}
        label={t("notifications.actions.reopen")}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onReopen?.(entry);
        }}
      />
    );
  }

  const preview = entry.messagePreview?.trim();

  return (
    <ListItem
      asChild
      leadingIcon={REASON_ICON[entry.reason] ?? Bell}
      title={
        entry.conversationTitle?.trim()
          ? entry.conversationTitle
          : t("tickets.meta.noSubject")
      }
      titleLines={2}
      description={
        <>
          {reasonText(t, entry)}
          {" · "}
          <time
            dateTime={time ?? undefined}
            title={formatDateTime(time, true)}
            className="whitespace-nowrap"
          >
            {formatRelativeDateShort(time, t("notifications.justNow"))}
          </time>
        </>
      }
      descriptionLines={2}
      excerpt={
        preview ? t("notifications.preview", { text: preview }) : undefined
      }
      trailing={trailing}
      actions={actions}
      onClick={(event) => onSelect(entry, event)}
    >
      <Link to={entryLink(entry)} />
    </ListItem>
  );
}
